import { Link } from 'react-router-dom';
import { ArrowRight, Check, CircleCheckBig, type LucideIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { SeoHead } from './SeoHead';
import { Helmet } from 'react-helmet-async';
import { getServiceSchema } from '../seo/schema';

export type SolutionPageProps = {
  title: string;
  metaTitle: string;
  description: string;
  path: string;
  heroCopy: string;
  statement: string;
  capabilities: Array<{ title: string; copy: string; icon: LucideIcon }>;
  workflow: Array<{ title: string; copy: string }>;
  bestFor: string[];
  boundaries: string[];
  included: string[];
  recordLabel: string;
  recordRows: Array<[string, string]>;
};

export function SolutionPage({
  title,
  metaTitle,
  description,
  path,
  heroCopy,
  statement,
  capabilities,
  workflow,
  bestFor,
  boundaries,
  included,
  recordLabel,
  recordRows,
}: SolutionPageProps) {
  return (
    <div className="shoreline-solution-page">
      <SeoHead title={metaTitle} description={description} path={path} />
      <Helmet><script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getServiceSchema({ serviceType: title, description, urlPath: path })) }} /></Helmet>

      <section className="shoreline-solution-hero relative overflow-hidden bg-verto-black py-16 text-verto-white md:py-24">
        <div className="page-wrap grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <h1 className="display-title max-w-[13ch] text-verto-white">{title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-verto-white/65">{heroCopy}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg"><Link to="/free-demo">Talk with Nate <ArrowRight className="ml-2 h-5 w-5" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/35 text-white hover:bg-white hover:text-verto-black"><Link to="/demos">See Website Work</Link></Button>
            </div>
          </div>
          <div className="shoreline-system-brief paper-record bg-verto-fog p-7 text-verto-black sm:p-9">
            <div className="flex items-start justify-between gap-5">
              <div><p className="text-sm font-bold text-verto-deep">{recordLabel.replace(' record', '')}</p><h2 className="mt-2 font-display text-4xl font-bold leading-none">What this includes</h2></div>
              <span className="stamp">Mapped</span>
            </div>
            <dl className="mt-8 grid gap-3">
              {recordRows.map(([term, value]) => <div key={term} className="grid gap-1 border-b border-verto-black/15 pb-3 sm:grid-cols-[8rem_1fr]"><dt className="text-xs font-bold uppercase tracking-[0.1em] text-verto-black/45">{term}</dt><dd className="text-sm font-semibold">{value}</dd></div>)}
            </dl>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="page-wrap grid gap-12 lg:grid-cols-[0.75fr_1.25fr]">
          <div><h2 className="section-title">{statement}</h2></div>
          <div className="divide-y divide-verto-black/15 border-y border-verto-black/15">
            {capabilities.map(({ title: capabilityTitle, copy, icon: Icon }) => (
              <div key={capabilityTitle} className="grid gap-4 py-7 sm:grid-cols-[3rem_1fr]">
                <Icon className="h-7 w-7 text-verto-deep" />
                <div><h3 className="font-display text-3xl font-bold">{capabilityTitle}</h3><p className="mt-2 max-w-2xl text-sm leading-6 text-verto-black/60">{copy}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-verto-black/15 bg-verto-white py-20 md:py-24">
        <div className="page-wrap">
          <h2 className="section-title">How this piece joins the rest of the system.</h2>
          <div className="mt-10 grid gap-px overflow-hidden rounded-xl bg-verto-black/15 md:grid-cols-4">
            {workflow.map((step, index) => (
              <div key={step.title} className={`${index === 0 ? 'bg-verto-fog' : index === 2 ? 'bg-verto-mint' : 'bg-verto-white'} min-h-56 p-7`}>
                <p className="font-display text-base font-bold text-verto-deep">{String(index + 1).padStart(2, '0')}</p>
                <h3 className="mt-10 font-display text-3xl font-bold">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-verto-black/60">{step.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="page-wrap grid gap-8 lg:grid-cols-2">
          <div className="rounded-xl bg-verto-black p-8 text-verto-white md:p-10">
            <h2 className="font-display text-4xl font-bold">Good fit when…</h2>
            <ul className="mt-7 space-y-4">
              {bestFor.map((item) => <li key={item} className="flex gap-3 text-sm leading-6 text-verto-white/70"><CircleCheckBig className="mt-0.5 h-5 w-5 shrink-0 text-verto-mint" />{item}</li>)}
            </ul>
          </div>
          <div className="shoreline-system-brief paper-record bg-verto-mint p-8 md:p-10">
            <h2 className="font-display text-4xl font-bold">Clear boundaries</h2>
            <ul className="mt-7 space-y-4">
              {boundaries.map((item) => <li key={item} className="flex gap-3 text-sm leading-6 text-verto-black/65"><Check className="mt-0.5 h-5 w-5 shrink-0" />{item}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-verto-black py-20 text-verto-white">
        <div className="page-wrap grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div><h2 className="section-title">What gets scoped before launch.</h2><p className="mt-5 max-w-xl text-sm leading-6 text-verto-white/60">You see the deliverables, responsibilities, usage costs, and handoff rules before the implementation begins.</p></div>
          <div className="grid gap-3 sm:grid-cols-2">
            {included.map((item) => <div key={item} className="flex items-center gap-3 border-b border-white/15 py-3 text-sm font-semibold"><Check className="h-4 w-4 text-verto-mint" />{item}</div>)}
          </div>
        </div>
      </section>

      <section className="bg-verto-mint py-16 md:py-20">
        <div className="page-wrap flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div><h2 className="section-title">Start with the leak, not the software.</h2><p className="mt-5 max-w-2xl text-base leading-7 text-verto-black/65">We will trace the current path and tell you which part is worth fixing first.</p></div>
          <Button asChild size="lg"><Link to="/free-demo">Talk with Nate <ArrowRight className="ml-2 h-5 w-5" /></Link></Button>
        </div>
      </section>
    </div>
  );
}
